import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy — Nexus';
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 672,
        mx: 'auto',
        p: 3,
        pb: 6,
        color: 'text.primary',
        '& ul': { pl: 2.5 },
        '& li': { mb: 0.75 },
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Effective date:</strong> March 30, 2026 · <strong>Service:</strong> Nexus (“Nexus”, “we”,
        “us”) operated by Digital Dynamic Solution.
      </Typography>
      <Typography paragraph>
        This policy describes how we collect, use, store, and share information when you use Nexus. You{' '}
        <strong>sign in to Nexus with email and password</strong>. If you connect social platforms, we
        process additional data as described below—
        <strong>
          Meta (Facebook / Instagram) is used to connect Pages and Instagram Business accounts for publishing
          and related features, not as your Nexus login.
        </strong>
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        1. Information we collect
      </Typography>
      <Box component="ul">
        <Typography component="li" paragraph>
          <strong>Nexus account (sign-in):</strong> name, email address, username, and a{' '}
          <strong>password stored using one-way hashing</strong> (we do not store your password in plain
          text). This is how you authenticate into Nexus.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Meta connection (Facebook Pages / Instagram Business):</strong> If you choose to connect
          Meta, you complete Meta’s authorization flow so Nexus can access{' '}
          <strong>Facebook Pages and Instagram Business accounts</strong> you select. We receive access
          tokens, Page and Instagram business identifiers, and related metadata or profile fields Meta
          provides for those connected assets. We may also store <strong>which Page you assign to which
          client</strong> in Nexus and <strong>engagement metrics</strong> Meta makes available for your
          connected content (for example like, comment, and share counts on posts published or managed through
          Nexus) for analytics and history inside the product. This is <strong>separate from Nexus sign-in</strong>{' '}
          and is used only to operate connected-social features.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Other connected platforms:</strong> If you connect other providers (for example LinkedIn,
          YouTube, X), we receive tokens and account identifiers you authorize for those integrations.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Content and activity in Nexus:</strong> content and metadata you create, schedule, or
          manage through Nexus (for example posts, captions, media, and engagement-related data needed to
          provide the service).
        </Typography>
        <Typography component="li" paragraph>
          <strong>Technical data:</strong> such as IP address, device/browser type, and logs used for
          security, debugging, and service reliability.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Optional Instagram “manual” credentials:</strong> If you use the optional manual Instagram
          connection (instead of using Meta’s APIs after connecting a Facebook Page with a linked Instagram
          Business account), you may provide your Instagram username and password. We store the password{' '}
          <strong>encrypted at rest</strong> and use the username and decrypted password only to perform
          actions you request through <strong>browser-based automation</strong> (for example automated login
          and posting via our Puppeteer-based service when the official Instagram Graph API path is not used
          for your account).
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        2. How we use information
      </Typography>
      <Box component="ul">
        <Typography component="li" paragraph>
          To provide, maintain, and improve Nexus (including scheduling, publishing where you instruct us,
          and account management).
        </Typography>
        <Typography component="li" paragraph>
          To authenticate you and protect accounts against fraud and abuse.
        </Typography>
        <Typography component="li" paragraph>
          To communicate with you about the service (for example, security notices and support responses).
        </Typography>
        <Typography component="li" paragraph>
          To comply with law and enforce our terms.
        </Typography>
        <Typography component="li" paragraph>
          To run <strong>optional browser automation</strong> for Instagram only when you have used manual
          connect and supplied credentials, as described above. The <strong>recommended</strong> way to use
          Nexus with Instagram is to <strong>connect Meta for a Facebook Page that has a linked Instagram
          Business account</strong>, so posting can use Meta’s supported APIs without storing your Instagram
          password in Nexus.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        3. Legal bases (where applicable)
      </Typography>
      <Typography paragraph>
        Depending on your region, we rely on appropriate bases such as performance of a contract,
        legitimate interests (for example securing the service), and consent where required for specific
        processing.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        4. Sharing
      </Typography>
      <Typography paragraph>
        We do not sell your personal information. We share information with service providers who help us
        host and operate the service (for example cloud hosting), and with platforms when you direct us to
        connect or publish on your behalf. Those platforms process data under their own policies.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        5. Retention and deletion
      </Typography>
      <Typography paragraph>
        We retain information for as long as your account is active and as needed to provide the service,
        comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion or
        account closure as described on our{' '}
        <Link component={RouterLink} to="/data-deletion.html">
          User Data Deletion
        </Link>{' '}
        page (HTTPS). <strong>Today, email is the primary channel</strong> for those requests; self-service
        tools in Settings may be added over time.
      </Typography>
      <Typography paragraph>
        You can <strong>disconnect Nexus from Meta</strong> in your Facebook / Instagram app and website
        settings, which stops <strong>new</strong> access through that connection; data already stored in
        Nexus may remain until you request deletion as above or we complete a valid Meta{' '}
        <strong>data deletion</strong> request (see the User Data Deletion page for how Meta-originated
        requests are handled).
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        6. Security
      </Typography>
      <Typography paragraph>
        We use reasonable administrative, technical, and organizational measures designed to protect
        information. No method of transmission or storage is 100% secure.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        7. Your rights
      </Typography>
      <Typography paragraph>
        Depending on where you live, you may have rights to access, correct, delete, or export personal
        information, or to object to or restrict certain processing. To exercise these rights, contact us
        using the details below.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        8. Children
      </Typography>
      <Typography paragraph>
        Nexus is not directed to children under 13 (or the minimum age required in your jurisdiction), and
        we do not knowingly collect personal information from children.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        9. International transfers
      </Typography>
      <Typography paragraph>
        If you access Nexus from outside the country where our servers or vendors are located, your
        information may be transferred and processed across borders. We take steps designed to ensure
        appropriate safeguards where required.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        10. Changes
      </Typography>
      <Typography paragraph>
        We may update this policy from time to time. We will post the updated version on this page and adjust
        the effective date.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        11. Nexus sign-in vs. Meta (Facebook / Instagram)
      </Typography>
      <Typography paragraph>
        <strong>Nexus sign-in</strong> is <strong>email and password only</strong> for your Nexus workspace
        account. <strong>Meta</strong> is used when you <strong>optionally connect Facebook Pages and
        Instagram Business accounts</strong> so Nexus can publish or read insights on your behalf.
        Completing Meta’s authorization for that purpose does not replace your Nexus credentials.
      </Typography>
      <Typography paragraph>
        <strong>Optional manual Instagram connect</strong> (browser automation with stored credentials) may
        be restricted or prohibited under <strong>Meta’s Platform Terms</strong>, Developer Policies, and
        Instagram’s terms, regardless of whether a specific feature is submitted for Meta App Review. Manual
        connect exists as an <strong>optional</strong> path; you and your organization are responsible for
        ensuring your use complies with applicable platform rules. Service operators may turn off manual
        connect entirely by configuration (for example environment settings that disable the manual-connect
        endpoint).
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        12. Contact
      </Typography>
      <Typography>
        For privacy questions or requests, contact:{' '}
        <Link href="mailto:luthdigitalconsult@gmail.com">luthdigitalconsult@gmail.com</Link>
      </Typography>
    </Box>
  );
}
